// api.js — fetch helpers for the dashboard's calls to the daemon /
// coordinator.
//
// Four variants for different needs:
//
//   api(path, opts)          GETs that throw on non-2xx. Returns
//                            parsed JSON. The 90% case.
//   apiWithMessage(path, opts)
//                            Like api() but extracts {message,error}
//                            from the response body on failure for
//                            better UX.
//   apiFetch(path, opts)     Passthrough that returns the raw
//                            Response. Caller handles non-2xx.
//                            Used by mutating buttons that want to
//                            check status codes themselves.
//   apiAsUser(path, opts)    Uses the Supabase USER JWT instead of
//                            the team token. Required for user-
//                            scoped endpoints (/v1/me/*, invite
//                            accept).
//
// Auth resolution:
// - Daemon mode (FEATURES.auth_screen === false): no bearer; the
//   daemon's own middleware decides.
// - Cloud mode: prefer Supabase JWT (rotates behind the scenes),
//   fall back to state.token for CLI-linked sessions. Sends to
//   COORDINATOR (an absolute URL in cloud-pages mode, "" same-
//   origin otherwise).
//
// On 401, clears the cached team token and calls showAuth() — both
// reads from window.AC_LEGACY (state.token via bridge, showAuth
// directly) since those still live in bootstrap.js.

import { state } from "./state.js";

// COORDINATOR is the base URL the bearer-aware calls hit. Read
// from window.AC_CONFIG at module-init time. Daemon mode leaves
// AC_CONFIG.API_BASE empty so requests stay same-origin.
const COORDINATOR =
  (window.AC_CONFIG && window.AC_CONFIG.API_BASE) || "";

// ---- Coordinator circuit breaker (quota-spiral guard) ----
// When the coordinator worker hits its Cloudflare plan limit it returns 429
// (Cloudflare's "rate limited" page, error 1027) for EVERY request — and that
// page carries no CORS headers, so in the browser each call surfaces as a
// network/CORS failure. The dashboard's pollers (overview counts+leases,
// handoffs, whoami, …) each retry on failure, so a rate-limited worker gets
// HAMMERED HARDER, burning more quota and keeping it limited — a feedback loop.
//
// The breaker stops that at the single choke point every call passes through.
// On a 429 (or a network throw, the cloud rate-limit signature) we OPEN the
// breaker: subsequent calls fast-fail WITHOUT touching the network for a
// cooldown, so quota stops burning and the worker can recover. Cooldown backs
// off as repeated trips happen, and resets after a clean success.
const COOLDOWN_BASE_MS = 30_000; // first trip: pause network for 30s
const COOLDOWN_MAX_MS = 5 * 60_000; // cap at 5 min
const _breaker = { openUntil: 0, trips: 0 };

function breakerOpen() {
  return Date.now() < _breaker.openUntil;
}
// isCoordinatorCooling is the exported read of the breaker state, so pollers
// can bail out of their own retry loops instead of relying solely on the
// per-call fast-fail throw.
export function isCoordinatorCooling() {
  return breakerOpen();
}
function tripBreaker() {
  _breaker.trips = Math.min(_breaker.trips + 1, 6);
  const cooldown = Math.min(
    COOLDOWN_BASE_MS * Math.pow(2, _breaker.trips - 1),
    COOLDOWN_MAX_MS,
  );
  _breaker.openUntil = Date.now() + cooldown;
  // eslint-disable-next-line no-console
  console.warn(
    `[api] coordinator rate-limited (429/quota). Pausing requests for ${Math.round(
      cooldown / 1000,
    )}s to avoid a retry storm. This is a Cloudflare Workers plan-limit, not an auth issue.`,
  );
}
function resetBreaker() {
  if (_breaker.trips !== 0 || _breaker.openUntil !== 0) {
    _breaker.trips = 0;
    _breaker.openUntil = 0;
  }
}
// RateLimitError is thrown when the breaker is open (or a 429 just landed) so
// callers/pollers can recognize it and back off instead of tight-looping.
// name-based so it survives message rewording, like AuthRedirectError.
export class RateLimitError extends Error {
  constructor(message) {
    super(message || "coordinator_rate_limited");
    this.name = "RateLimitError";
  }
}

// ---------------------------------------------------------------------------
// Login-flash guard (render-only; touches NO session state).
//
// The flash: on a logged-in reload, boot-time api() calls fire before the
// async Supabase client has hydrated the session, so _apiImpl sees no bearer
// and calls showAuth() — painting the login screen for a split second until
// the session resolves and the dashboard swaps in. A normal site decides auth
// BEFORE rendering an auth-dependent screen; we approximate that by suppressing
// the login reveal during early boot when the user is, in fact, logged in.
//
// We can tell synchronously: Supabase persists the session in localStorage
// under sb-<project-ref>-auth-token from the very first tick. If that key is
// present we are logged in (session merely loading), so DEFER showAuth() until
// the dashboard renders — we never reveal login behind a session that's about
// to resolve. This is READ-ONLY: it only reads localStorage and toggles a
// re-check timer. It never calls refreshSession()/signOut()/getSession() and
// never mutates a token, so unlike the reverted #351/#353 it cannot log anyone
// out. If the token is absent (genuinely logged out) we show login at once.
const SB_AUTH_STORAGE_KEY = "sb-sdkumimayzehxduacovy-auth-token";
const _bootStartedAt = Date.now();
const BOOT_AUTH_GRACE_MS = 4000;

function hasStoredSession() {
  try {
    const v = localStorage.getItem(SB_AUTH_STORAGE_KEY);
    return !!v && v !== "null";
  } catch {
    return false;
  }
}

// True once showDashboard() has run (the .app container is displayed). Lets a
// deferred showAuth() retry bail the moment the session hydrates and the
// dashboard renders, so the grace window never reveals login behind it.
function dashboardIsVisible() {
  try {
    const app = document.querySelector(".app");
    return !!app && getComputedStyle(app).display !== "none";
  } catch {
    return false;
  }
}

// showAuth is the legacy IIFE's auth-screen helper — still lives
// in bootstrap.js. We pull it through window.AC_LEGACY at
// call time so the import doesn't have to wait for setup.
//
// If the bridge isn't wired (very early boot, or a future refactor
// drops the export), warn loudly. The previous silent no-op left
// users with just an unexplained toast ("Save failed: unauthorized:
// no auth token available") and no way to recover.
function showAuth(_attempt) {
  // Render-only flash guard (see block above). During early boot, if a
  // stored session exists and the dashboard hasn't rendered yet, the user is
  // logged in and the session is still hydrating — re-check shortly instead of
  // flashing login. Bails the instant the dashboard shows (logged in) or the
  // stored token disappears (real logout).
  if (
    Date.now() - _bootStartedAt < BOOT_AUTH_GRACE_MS &&
    hasStoredSession() &&
    !dashboardIsVisible()
  ) {
    setTimeout(() => showAuth(_attempt), 150);
    return;
  }

  const legacy = window.AC_LEGACY;
  if (legacy && typeof legacy.showAuth === "function") {
    legacy.showAuth();
    return;
  }
  // Bridge not wired YET. This happens when an authed fetch 401s during
  // very early boot — before bootstrap.js's IIFE reaches the
  // window.AC_LEGACY.showAuth seed. It's a timing race, not a permanent
  // miss: the bridge appears within a few ms once the IIFE runs. So we
  // RETRY on a short interval rather than giving up and leaving the user
  // stuck on a dead screen. Cap the retries so a genuine regression
  // (bridge never seeded) still surfaces a console error instead of
  // spinning forever.
  const attempt = _attempt || 0;
  const MAX_ATTEMPTS = 25; // ~2.5s total at 100ms — far longer than boot
  if (attempt < MAX_ATTEMPTS) {
    setTimeout(() => showAuth(attempt + 1), 100);
    return;
  }
  // eslint-disable-next-line no-console
  console.error(
    "[api] showAuth bridge missing (window.AC_LEGACY.showAuth) after " +
      MAX_ATTEMPTS + " retries; user is stuck. This indicates a dashboard " +
      "boot-order regression — file an issue."
  );
}

// AuthRedirectError is the sentinel api() / apiWithMessage() throw
// when they're about to flip the screen to the auth surface (no
// bearer token available, or 401 from coordinator). Callers that
// would otherwise render an error toast on every caught exception
// should check `err.name === "AuthRedirectError"` and silently
// return — the user is already being shown the login form; an
// additional "Save failed: unauthorized: no auth token available"
// toast is confusing noise.
//
// Pre-this-class fix: api.js threw a plain Error with that string
// message. Every caller's catch block dutifully toasted it, so
// users got a misleading "Save failed" alert overlaying the auth
// screen and had no idea their save was discarded because of an
// expired Supabase session, not a server error.
export class AuthRedirectError extends Error {
  constructor(reason) {
    // Message keeps the literal "unauthorized" word so legacy regex
    // callers (worktrees.js, team-bus.js, team-roles.js, team-repos.js
    // all check `/unauthorized/i.test(msg)` to gate sign-in CTAs)
    // still match. New callers should prefer `err instanceof
    // AuthRedirectError`, which is robust to message rewording.
    super("unauthorized: " + (reason || "auth_redirect"));
    this.name = "AuthRedirectError";
  }
}

// getSupabase pulls the Supabase client from the legacy IIFE's
// `let supabase = ...` binding (reassigned during init). Reading
// directly from window.AC_LEGACY at call time means the api.js
// module doesn't have to know about init ordering.
function getSupabase() {
  const legacy = window.AC_LEGACY;
  if (!legacy || typeof legacy.getSupabase !== "function") return null;
  return legacy.getSupabase();
}

// isExpired reports whether a Supabase session's access token is past its
// expiry (with a small skew so a token about to expire mid-flight is treated
// as expired). expires_at is unix SECONDS. Missing expiry → treat as expired
// (safer to refresh than to send a token we can't validate).
function isExpired(session) {
  const exp = session && session.expires_at;
  if (!exp) return true;
  return exp * 1000 <= Date.now() + 5000;
}

// In-flight dedupe map. When the dashboard boots, multiple unrelated
// callers can hit the same GET endpoint within the same tick (e.g.
// /v1/worktrees?active=true fires from refreshWorktreesBadge,
// scheduleDynamic, AND the Worktrees page render). Each one runs the
// full network roundtrip + JSON parse. With a slow endpoint (the
// worktrees du-walk takes 8s on machines with lots of worktrees)
// this stacks to 30s+ of redundant work blocking the page.
//
// Key by full URL. Only dedupes simple GETs (no opts) — anything
// with custom headers/body/method falls through. Each value is the
// in-flight Promise; resolves clear the entry. Errors clear it
// immediately so callers can retry without a stale rejection.
const _inflightGets = new Map();

// The browser opens at most six HTTP/1.1 connections to the daemon. The
// daemon forwards /v1/teams/* and /v1/sync/* to the team server, so when that
// server hangs, those calls held all six and queued the daemon's own fast
// local calls behind them. Cap them at three so local pages always have a
// connection. A finishing call hands its slot straight to the next waiter.
const TEAM_PROXY_SLOTS = 3;
let teamProxyInFlight = 0;
const teamProxyWaiters = [];

export function isTeamProxyPath(path) {
  return path.startsWith("/v1/teams/") || path.startsWith("/v1/sync/");
}

export async function withTeamProxySlot(fn) {
  if (teamProxyInFlight < TEAM_PROXY_SLOTS) teamProxyInFlight++;
  else await new Promise((resolve) => teamProxyWaiters.push(resolve));
  try {
    return await fn();
  } finally {
    const next = teamProxyWaiters.shift();
    if (next) next();
    else teamProxyInFlight--;
  }
}

// api: the GET-shaped, throw-on-non-2xx variant.
export async function api(path, opts) {
  // Dedupe only the no-opts case — the common one for snapshot reads.
  if (!opts) {
    const existing = _inflightGets.get(path);
    if (existing) return existing;
    const p = _apiImpl(path, opts);
    _inflightGets.set(path, p);
    // Clear the entry the moment the underlying promise settles.
    // Using .then(cleanup, cleanup) instead of .finally() so the
    // cleanup chain itself doesn't introduce a new unhandled
    // rejection branch — .finally() returns a new Promise that
    // re-rejects if the source rejected, and that secondary
    // promise has no caller to consume it. Callers still receive
    // the original `p` (with its rejection intact) and handle it
    // via their own try/catch. PR #232 fix.
    const cleanup = () => _inflightGets.delete(path);
    p.then(cleanup, cleanup);
    return p;
  }
  return _apiImpl(path, opts);
}

async function _apiImpl(path, opts) {
  // `background: true` marks a call the user did not initiate — today the
  // handoffs badge prefetch and its 30s poller. A 401 on one of those must
  // not tear the session down; see the r.status === 401 branch below.
  //
  // Passing opts also opts the call out of the _inflightGets dedupe (which
  // only covers the no-opts case), so a background poll can never hand its
  // response — or its rejection — to a foreground caller.
  const background = !!(opts && opts.background);
  // Strip our own key before anything reaches fetch(). RequestInit ignores
  // unknown properties, so this is cosmetic, but leaving it in makes the
  // options object ambiguous for the next reader.
  let fetchOpts = opts;
  if (opts && "background" in opts) {
    fetchOpts = { ...opts };
    delete fetchOpts.background;
  }
  // Circuit breaker: if the coordinator just rate-limited us, fast-fail every
  // call without touching the network until the cooldown elapses. This is what
  // breaks the quota spiral — pollers that retry now get an instant throw
  // instead of another wasted request against a limited worker. Local-daemon
  // mode never trips the breaker (it doesn't go through the cloud 429 path),
  // but checking here is harmless: _breaker.openUntil only advances on a real
  // cloud 429/throw, so daemon mode's openUntil stays 0.
  if (breakerOpen()) {
    throw new RateLimitError("coordinator_cooldown");
  }
  // In local-daemon mode the dashboard talks to the loopback
  // daemon over a same-origin path. The daemon's auth is a
  // loopback token (or none in -auth=none mode); cloud's Supabase/
  // team-token plumbing doesn't apply. Skip the bearer dance and
  // let the daemon's own middleware decide. PR #7 sets
  // FEATURES.auth_screen=false in daemon mode.
  const cloudAuth =
    window.AC_CONFIG && window.AC_CONFIG.FEATURES.auth_screen;
  if (!cloudAuth) {
    const call = async () => {
      const r = await fetch(COORDINATOR + path, fetchOpts);
      if (!r.ok) throw new Error(path + ": " + r.status);
      return r.status === 204 ? null : await r.json();
    };
    return isTeamProxyPath(path) ? withTeamProxySlot(call) : call();
  }

  // Cloud-pages / coordinator-hosted: prefer Supabase JWT, fall
  // back to state.token for CLI-linked sessions.
  let bearer = state.token;
  // sessionExpired distinguishes "session ran out and could not be refreshed"
  // (→ must show login) from "session hasn't hydrated yet" (→ defer & retry).
  // Both leave bearer null, but only the former should paint the login screen.
  let sessionExpired = false;
  try {
    const sb = getSupabase();
    if (sb) {
      let {
        data: { session },
      } = await sb.auth.getSession();
      // getSession() returns a PRESENT-but-EXPIRED session when the cookie
      // ran out (Supabase doesn't auto-refresh on a bare getSession). Sending
      // that stale access_token makes the coordinator reject the request —
      // and if the rejection comes back as a CORS/network failure (not a clean
      // 401), the r.status===401 → showAuth() path below never runs and the
      // user is stuck on a dead screen with no way to log in. So: if expired,
      // try a refresh; if that fails (refresh token also dead), force the
      // login screen below — do NOT let the "hydrating" heuristic defer it
      // (the expired token is still in localStorage, which would otherwise
      // read as "session present, just loading").
      if (session && isExpired(session)) {
        try {
          const refreshed = await sb.auth.refreshSession();
          session = refreshed?.data?.session || null;
        } catch {
          session = null;
        }
        if (!session) {
          bearer = null;
          sessionExpired = true;
        }
      }
      if (session) bearer = session.access_token;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(
      "[api] supabase getSession failed, falling back to laptop token:",
      e,
    );
  }
  // Don't send 'Bearer null' if we have nothing.
  if (!bearer) {
    // EXPIRED session (cookie ran out, refresh failed above) → the stored
    // token is stale and unrecoverable. Show login NOW; do not fall into the
    // "hydrating" deferral below — the expired token is still in localStorage,
    // so the hasStoredSession heuristic would otherwise mistake this for a
    // boot race and leave the user stuck retry-looping with no login screen.
    if (sessionExpired) {
      showAuth();
      throw new AuthRedirectError("session_expired");
    }
    // Otherwise null bearer has two causes:
    //   1. Genuinely logged out — no stored session. Show login.
    //   2. Boot race — a logged-in user's Supabase session hasn't finished
    //      hydrating yet, so getSession() above transiently returned null on
    //      THIS call even though the session token is sitting in localStorage.
    //      This is the confirmed cause of "hard refresh logs me out": the
    //      dashboard renders (handleAuthSuccess saw a session), then a boot-
    //      time api() call races getSession()-not-ready, hits this null-bearer
    //      branch, and showAuth() flips a valid user back to login.
    // Distinguish by reading the persisted Supabase session synchronously. If
    // it's present, this is case 2 — do NOT show login; throw a transient
    // error and let the caller's poll retry once the session hydrates (a few
    // hundred ms). This is render/throw-only: it never refreshes, signs out,
    // or mutates the token, so it can't cause a logout.
    let hasStoredSession = false;
    try {
      const v = localStorage.getItem("sb-sdkumimayzehxduacovy-auth-token");
      hasStoredSession = !!v && v !== "null";
    } catch {
      /* no storage access → treat as logged out */
    }
    if (hasStoredSession) {
      throw new AuthRedirectError("session_hydrating");
    }
    showAuth();
    throw new AuthRedirectError("no_auth_token");
  }
  // Resolve the explicit tenant the user chose. Coordinator's JWT auth
  // defaults to ORDER BY linked_at ASC LIMIT 1 (coordinator.ts:1747), which
  // picks the OLDEST user_teams row — wrong when the user belongs to
  // multiple teams and the stored team token points at a newer one. X-Team-ID
  // is the documented override (coordinator.ts:1729-1744); the server
  // silently ignores it when the JWT user isn't a member of the requested
  // tenant, so no info leak. Persisted in localStorage as ac-tenant-id by
  // the auto-link flow in bootstrap.js so it's available on the very first
  // api() call (no chicken-and-egg with /v1/teams/info).
  const explicitTeamId =
    localStorage.getItem("ac-tenant-id") || state.teamInfo?.team_id || null;
  const headers = {
    ...(opts?.headers || {}),
    Authorization: "Bearer " + bearer,
  };
  if (explicitTeamId && !headers["X-Team-ID"]) {
    headers["X-Team-ID"] = explicitTeamId;
  }
  let r;
  try {
    r = await fetch(COORDINATOR + path, { ...fetchOpts, headers });
  } catch (netErr) {
    // A thrown fetch is a network/CORS-level failure — the response never
    // reached us. On the cloud coordinator the dominant cause is the
    // Cloudflare rate-limit page (429/error-1027), which has no CORS headers
    // and so surfaces as a TypeError here rather than a readable r.status.
    // Trip the breaker so the pollers stop hammering, then rethrow as a
    // RateLimitError so callers can recognize and back off.
    tripBreaker();
    throw new RateLimitError("coordinator_unreachable");
  }
  if (r.status === 429) {
    // Explicit rate-limit (worker-emitted, or a Cloudflare 429 that did reach
    // us). Same spiral guard.
    tripBreaker();
    throw new RateLimitError("coordinator_429");
  }
  if (r.status === 401) {
    // A background call can legitimately race ahead of the flow that creates
    // the user_teams row the coordinator's JWT auth requires: a first-run user
    // is still in onboarding when the badge prefetch fires, and
    // createSoloWorkspace has not called /v1/teams/link yet. Tearing the
    // session down here deleted the team token onboarding had just written,
    // and only the render order of showDashboard() after showAuth() kept the
    // user on the dashboard at all.
    //
    // So background callers get the rejection and nothing else. The next
    // user-initiated call re-evaluates auth for real, so a genuinely dead
    // session still reaches the login screen.
    if (!background) {
      localStorage.removeItem("ac-team-token");
      localStorage.removeItem("ac-tenant-id");
      state.token = null;
      showAuth();
    }
    throw new AuthRedirectError("coordinator_401");
  }
  if (!r.ok) throw new Error(path + ": " + r.status);
  // A clean response means the worker is healthy again — close the breaker.
  resetBreaker();
  return r.status === 204 ? null : await r.json();
}

// apiAsUser: like api() but uses the Supabase user JWT. Required
// for user-scoped endpoints that the team token can't reach.
export async function apiAsUser(path, opts) {
  const sb = getSupabase();
  if (!sb) throw new Error("Not signed in");
  const {
    data: { session },
  } = await sb.auth.getSession();
  if (!session)
    throw new Error(
      "No user session — sign in with email and password to use this feature",
    );
  const headers = {
    ...(opts?.headers || {}),
    Authorization: "Bearer " + session.access_token,
  };
  // Same X-Team-ID resolution as api()/apiFetch. Critical for endpoints
  // that read tenant-scoped state — without this, multi-team users
  // hit whichever tenant has the oldest user_teams row.
  const explicitTeamId =
    localStorage.getItem("ac-tenant-id") || state.teamInfo?.team_id || null;
  if (explicitTeamId && !headers["X-Team-ID"]) {
    headers["X-Team-ID"] = explicitTeamId;
  }
  const r = await fetch(COORDINATOR + path, { ...opts, headers });
  const data = await r.json().catch(() => ({}));
  if (!r.ok)
    throw new Error(data.message || data.error || "HTTP " + r.status);
  return data;
}

// apiFetch: passthrough that returns the raw Response. Use this
// for endpoints called from buttons where the caller wants to
// branch on status codes (e.g. DELETE returning 204, conflict
// detection on 409, etc). For data-shape GETs, prefer api().
//
// Unlike api()/apiWithMessage(), this does NOT require a bearer to
// be present: local-daemon mode has no auth and the daemon ignores
// Authorization headers. Cloud mode attaches the team token if
// present (and the request 401s if it isn't, which is the right
// outcome).
export async function apiFetch(path, opts) {
  let bearer = state.token;
  try {
    const sb = getSupabase();
    if (sb) {
      const {
        data: { session },
      } = await sb.auth.getSession();
      if (session) bearer = session.access_token;
    }
  } catch {
    // best-effort
  }
  const headers = { ...(opts?.headers || {}) };
  if (bearer && !headers["Authorization"])
    headers["Authorization"] = "Bearer " + bearer;
  // Same X-Team-ID resolution as api() (api.js:60+). apiFetch had the
  // same wrong-tenant bug — JWT auth defaults to the oldest user_teams
  // row, so writes like rule delete / template install land on the
  // wrong tenant for users with multiple teams. PR #212 fixed api(),
  // this catches up apiFetch.
  const explicitTeamId =
    localStorage.getItem("ac-tenant-id") || state.teamInfo?.team_id || null;
  if (explicitTeamId && !headers["X-Team-ID"]) {
    headers["X-Team-ID"] = explicitTeamId;
  }
  return fetch(COORDINATOR + path, { ...opts, headers });
}

// apiWithMessage: like api() but extracts {message, error} from
// the response body on failure. Use this when the UX wants to
// show a server-provided message ("Invite already redeemed") vs
// a bare HTTP code.
export async function apiWithMessage(path, opts) {
  // Local-daemon mode has no sign-in: the daemon's own middleware decides,
  // exactly as in _apiImpl. Without this branch every caller fell into the
  // cloud no-bearer path below and swapped the dashboard for the login screen.
  const cloudAuth =
    window.AC_CONFIG && window.AC_CONFIG.FEATURES.auth_screen;
  if (!cloudAuth) {
    const call = async () => {
      const r = await fetch(COORDINATOR + path, opts);
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw errorFromBody(r, data);
      return data;
    };
    return isTeamProxyPath(path) ? withTeamProxySlot(call) : call();
  }
  let bearer = state.token;
  try {
    const sb = getSupabase();
    if (sb) {
      const {
        data: { session },
      } = await sb.auth.getSession();
      if (session) bearer = session.access_token;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(
      "[api] supabase getSession failed, falling back to laptop token:",
      e,
    );
  }
  // Defend against bearer === null (no laptop token + supabase
  // fetch failed): sending 'Bearer null' to the coordinator would
  // yield a 401, which is technically correct but produces a
  // confusing UX. Bail with an explicit unauthorized so the caller
  // knows it's an auth-state problem, not a server error.
  if (!bearer) {
    showAuth();
    throw new AuthRedirectError("no_auth_token");
  }
  const headers = {
    ...(opts?.headers || {}),
    Authorization: "Bearer " + bearer,
  };
  const r = await fetch(COORDINATOR + path, { ...opts, headers });
  const data = await r.json().catch(() => ({}));
  if (r.status === 401) {
    localStorage.removeItem("ac-team-token");
    localStorage.removeItem("ac-tenant-id");
    state.token = null;
    showAuth();
    throw new AuthRedirectError("coordinator_401");
  }
  // PR D Free-tier upgrade flow: 402 upgrade_required from the
  // coordinator means a Free-tier user tried to use a Team feature.
  // The handler in coordinator.ts's requireTeamTier() returns a
  // structured body; attach it to the thrown Error so callers can
  // distinguish "feature gated" from a generic server error and
  // render the upgrade modal instead of a toast.
  //
  // Sweep finding (post-PR D): scope this to upgrade_required only.
  // Other 402 shapes (e.g., seat_cap_exceeded from createInvitations)
  // have a different structure and the existing callers handle them
  // via the legacy throw path below. If a future 402 path needs
  // structured handling, add another branch here rather than
  // overloading upgradeRequired.
  if (r.status === 402 && data && typeof data === "object" && data.error === "upgrade_required") {
    const err = new Error(data.message || "Upgrade required");
    err.upgradeRequired = true;
    err.action = data.action;            // "upgrade_tier" | "buy_more_seats"
    err.upgradeTo = data.upgrade_to;     // DB identifier: 'startup' | 'enterprise'
    err.currentPlan = data.current_plan; // "free" | "team" | etc.
    err.feature = data.feature;          // "Custom workspaces" | "Custom roles" | etc.
    throw err;
  }
  if (!r.ok) throw errorFromBody(r, data);
  return data;
}

// errorFromBody attaches the machine-readable error code (additive) so
// callers can branch on specific failures (e.g. change-plan's
// no_subscription fallback) without matching on human-facing message text.
function errorFromBody(r, data) {
  const err = new Error(data.message || data.error || r.statusText || "HTTP " + r.status);
  err.code = data.error;
  return err;
}
